/**
 * @naar/shared — contrato único entre la API y la web.
 *
 * Convenciones:
 *  - El dinero viaja como `string` (`Money`), nunca como `number`. En la base es
 *    `numeric(14,2)` y en la API un `Decimal`; convertirlo a `number` en la frontera JSON
 *    reintroduciría el error de coma flotante que el esquema evita a propósito.
 *  - Los porcentajes viajan como `number` en tanto por ciento ya redondeado (`6.8` = 6,8 %).
 *  - Las fechas de negocio viajan como `YYYY-MM-DD` (`IsoDate`) y los instantes del sistema
 *    como ISO-8601 completo (`IsoDateTime`).
 */

// ---------------------------------------------------------------------------
// Primitivas
// ---------------------------------------------------------------------------

/** Importe decimal serializado como string. Ej.: `"1250.00"`. */
export type Money = string;

/** Fecha de negocio sin zona horaria. Ej.: `"2025-08-01"`. */
export type IsoDate = string;

/** Instante del sistema con zona horaria. Ej.: `"2025-08-01T10:22:31.000Z"`. */
export type IsoDateTime = string;

/** Mes de calendario. Ej.: `"2025-08"`. */
export type IsoMonth = string;

export type Uuid = string;

// ---------------------------------------------------------------------------
// Enumeraciones (espejo de los enums nativos de Postgres)
// ---------------------------------------------------------------------------

export type OrgPlan = 'free' | 'pro' | 'business';
export type UserRole = 'admin' | 'owner' | 'manager' | 'tenant';
export type PropertyType = 'piso' | 'casa' | 'local' | 'edificio' | 'habitaciones';
export type PropertyStatus = 'activa' | 'en_reforma' | 'vendida';
export type UnitType = 'habitacion' | 'apartamento' | 'completo';
export type UnitStatus = 'libre' | 'ocupada' | 'no_disponible';
export type ValuationSource = 'ia' | 'tasacion' | 'manual' | 'portal';
export type RateType = 'fija' | 'variable' | 'mixta';
export type ExpenseFrequency = 'mensual' | 'trimestral' | 'anual';
export type IdDocumentType = 'dni' | 'nie' | 'pasaporte';
export type LeaseStatus = 'borrador' | 'activo' | 'vencido' | 'rescindido' | 'renovado';
export type DepositStatus = 'pendiente' | 'depositado' | 'devuelto' | 'retenido';
export type RentChargeStatus =
  | 'pendiente'
  | 'emitido'
  | 'cobrado'
  | 'parcial'
  | 'impagado'
  | 'anulado';
export type PaymentMethod =
  | 'transferencia'
  | 'domiciliacion'
  | 'efectivo'
  | 'tarjeta'
  | 'bizum';
export type BankProvider = 'manual' | 'csv' | 'psd2';
export type CategoryKind = 'ingreso' | 'gasto';
export type TransactionSource = 'manual' | 'csv' | 'psd2';
export type ReviewStatus = 'ok' | 'necesita_revision' | 'revisado';
export type IssuePriority = 'baja' | 'media' | 'alta' | 'urgente';
export type IssueStatus = 'abierta' | 'en_curso' | 'resuelta' | 'cancelada';
export type DocumentCategory =
  | 'Contratos'
  | 'Inmueble'
  | 'Reparaciones'
  | 'Fiscal'
  | 'Nominas'
  | 'Identidad'
  | 'Pasaporte'
  | 'Auditoria'
  | 'Renovacion'
  | 'Rescision'
  | 'Otros';
export type DocumentSource = 'upload' | 'email' | 'api';
export type OcrStatus = 'pendiente' | 'procesando' | 'completado' | 'fallido' | 'no_aplica';
export type AlertType =
  | 'impago'
  | 'vencimiento_contrato'
  | 'vencimiento_seguro'
  | 'oportunidad_renta'
  | 'revalorizacion'
  | 'mantenimiento'
  | 'fiscal';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertStatus = 'nueva' | 'vista' | 'descartada' | 'resuelta';

/**
 * Mapa de color de la UI por severidad de alerta. Vive en `shared` y no en la API porque el
 * color es una decisión de presentación: la API devuelve `severity` y la web resuelve el
 * acento. Los valores son los del maquetado original.
 */
export const ALERT_SEVERITY_COLOR: Record<AlertSeverity, string> = {
  critical: '#9c2b3b',
  warning: '#a5801f',
  info: '#3f8a5c',
};

// ---------------------------------------------------------------------------
// Envoltorios de respuesta
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    /** Detalle de validación, solo en errores 400. */
    details?: unknown;
  };
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  uptimeSeconds: number;
  version: string;
  database: { connected: boolean; latencyMs: number | null };
  timestamp: IsoDateTime;
}

// ---------------------------------------------------------------------------
// Bloques reutilizables
// ---------------------------------------------------------------------------

/** Valor + su variación, tal y como lo pinta una KpiCard. */
export interface Trend {
  /** Variación en puntos porcentuales o en %, según el KPI. */
  percent: number | null;
  /** Variación en valor absoluto, si aplica. */
  amount: Money | null;
  direction: 'up' | 'down' | 'flat';
}

export interface PropertyRef {
  id: Uuid;
  name: string;
  city: string;
}

export interface UnitRef {
  id: Uuid;
  code: string;
}

// ---------------------------------------------------------------------------
// GET /api/dashboard/summary
// ---------------------------------------------------------------------------

export interface MonthlyCashflowPoint {
  month: IsoMonth;
  /** Etiqueta corta en español: `Ene`, `Feb`… */
  label: string;
  income: Money;
  expenses: Money;
  net: Money;
  /** Altura de la barra en %, ya normalizada contra el mes de mayor volumen. */
  incomeHeightPct: number;
  expensesHeightPct: number;
}

export interface PropertyComparisonRow {
  id: Uuid;
  name: string;
  city: string;
  /** Rentabilidad bruta anual en %. */
  grossYield: number;
  /** Anchura de la barra en %, normalizada contra la mejor propiedad. */
  barPct: number;
  rank: number;
  marketValue: Money;
  monthlyRent: Money;
}

export interface RentPerBedroomBand {
  bedrooms: number;
  label: string;
  averageRent: Money;
}

export interface Insight {
  id: string;
  title: string;
  body: string;
}

export interface DashboardSummaryResponse {
  /** Fecha de referencia formateada en español, ej. `3 de julio`. */
  today: string;
  greeting: string;
  kpis: {
    portfolioValue: Money;
    portfolioValueTrend: Trend;
    assetReturn: number;
    assetReturnGain: Money;
    occupancyRate: number;
    occupiedUnits: number;
    totalUnits: number;
    monthlyIncome: Money;
    netCashFlow: Money;
    outstandingDebt: Money;
    propertyCount: number;
    unitCount: number;
  };
  cashflow: MonthlyCashflowPoint[];
  comparison: PropertyComparisonRow[];
  rentPerBedroom: {
    average: Money;
    trend: Trend;
    bands: RentPerBedroomBand[];
  };
  insights: Insight[];
}

// ---------------------------------------------------------------------------
// GET /api/dashboard/performance
// ---------------------------------------------------------------------------

export interface EquityCurvePoint {
  month: IsoMonth;
  label: string;
  marketValue: Money;
  debt: Money;
  equity: Money;
  /** Altura de la barra en %, normalizada contra el máximo de la serie. */
  heightPct: number;
}

export interface DashboardPerformanceResponse {
  netWorth: Money;
  netWorthTrend: Trend;
  equityCurve: EquityCurvePoint[];
  roe: number;
  roeBenchmarkPercentile: number;
  averageCapRate: number;
  propertyCount: number;
  ranking: PropertyComparisonRow[];
}

// ---------------------------------------------------------------------------
// GET /api/dashboard/analytics
// ---------------------------------------------------------------------------

export interface ExpenseDistributionSlice {
  categoryId: Uuid | null;
  label: string;
  amount: Money;
  percent: number;
  color: string;
}

export interface OccupancyPoint {
  month: IsoMonth;
  label: string
  occupancyRate: number;
  occupiedUnits: number;
  totalUnits: number;
  /** Altura de la barra en %. */
  heightPct: number;
}

export interface DashboardAnalyticsResponse {
  expenseDistribution: ExpenseDistributionSlice[];
  /** `conic-gradient(...)` listo para el atributo style del donut. */
  expenseDonutGradient: string;
  occupancyTrend: OccupancyPoint[];
  metrics: {
    incomePerSqm: Money;
    vacantDaysPerYear: number;
    averageIssueCost: Money | null;
    expenseToIncomeRatio: number;
  };
}

// ---------------------------------------------------------------------------
// GET /api/portfolios  ·  GET /api/properties
// ---------------------------------------------------------------------------

export interface PortfolioSummary {
  id: Uuid;
  name: string;
  description: string | null;
  sortOrder: number;
  propertyCount: number;
  marketValue: Money;
  equity: Money;
}

export interface PropertyListRow {
  id: Uuid;
  portfolioId: Uuid;
  portfolioName: string;
  name: string;
  city: string;
  province: string | null;
  status: PropertyStatus;
  propertyType: PropertyType;
  units: number;
  occupiedUnits: number;
  /**
   * PERSONAS con contrato ACTIVO, no unidades ocupadas.
   *
   * No es lo mismo y por eso son dos campos: una habitación puede tener dos titulares
   * solidarios, así que «3 unidades ocupadas» pueden ser cuatro inquilinos. Derivarlo de la
   * ocupación daría un número que no cuadra con el listado de Inquilinos.
   */
  tenantCount: number;
  /** Unidades sin contrato activo: las plazas que quedan por alquilar. */
  freeUnits: number;
  occupancyRate: number;
  acquisitionPrice: Money;
  marketValue: Money;
  debt: Money;
  equity: Money;
  monthlyRent: Money;
  grossYield: number;
  /**
   * Portada para el listado: la miniatura de la foto marcada como portada.
   *
   * Va en la fila y no se pide aparte porque el listado la necesita SIEMPRE y una petición por
   * inmueble serían veinticinco peticiones para pintar una tabla. `null` cuando el inmueble no
   * tiene fotos todavía; entonces la interfaz pinta el marcador de posición.
   */
  cover: { photoId: Uuid; url: string | null; placeholderColor: string | null } | null;
  /** Cuántas fotos tiene. Es lo que decide si el selector de portada tiene algo que ofrecer. */
  photoCount: number;
}

export interface PropertyGroup {
  portfolio: { id: Uuid; name: string; description: string | null };
  properties: PropertyListRow[];
  totals: {
    units: number;
    acquisitionPrice: Money;
    marketValue: Money;
    equity: Money;
  };
}

export interface PropertiesResponse {
  groups: PropertyGroup[];
  meta: PaginationMeta;
}

// ---------------------------------------------------------------------------
// GET /api/properties/:id
// ---------------------------------------------------------------------------

export interface PropertyPhotoDto {
  id: Uuid;
  storageKey: string;
  isCover: boolean;
  sortOrder: number;
  /**
   * URL prefirmada de lectura, generada DESPUÉS de comprobar autorización (fase 2). Sigue
   * siendo `null` cuando el objeto no está en el almacén, y entonces la UI pinta el marcador
   * de posición del maquetado.
   */
  url: string | null;
  caption?: string | null;
  width?: number | null;
  height?: number | null;
  sizeBytes?: number | null;
  mimeType?: string | null;
  /** Color medio: se pinta mientras carga la imagen real y evita el parpadeo en blanco. */
  placeholderColor?: string | null;
  renditions?: { thumb: string | null; medium: string | null; large: string | null };
}

export interface UnitDto {
  id: Uuid;
  code: string;
  unitType: UnitType;
  areaM2: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  baseRent: Money;
  status: UnitStatus;
  floor?: string | null;
  orientation?: string | null;
  hasPrivateBathroom?: boolean;
  furnished?: boolean;
  notes?: string | null;
  currentLease: {
    id: Uuid;
    monthlyRent: Money;
    startDate: IsoDate;
    endDate: IsoDate | null;
    status: LeaseStatus;
    tenants: { id: Uuid; fullName: string; initials: string; isPrimary: boolean }[];
  } | null;
}

export interface MonthlyExpenseLine {
  concept: string;
  amount: Money;
  /** Origen del importe, para poder auditar el desglose del panel. */
  origin: 'loan' | 'recurring' | 'insurance';
  frequency: ExpenseFrequency;
  /** Importe tal cual se factura, antes de normalizar a mensual. */
  rawAmount: Money;
}

export interface ValuationDto {
  id: Uuid;
  valuedAt: IsoDate;
  value: Money;
  source: ValuationSource;
  confidence: number | null;
  notes: string | null;
}

/**
 * Financiación del inmueble: préstamos, seguros y gastos recurrentes CON SU IDENTIFICADOR.
 *
 * `expenseBreakdown` ya devolvía estos importes, pero agregados y sin `id`: sirve para
 * PINTAR el desglose y no para editarlo. Sin identificador, la pestaña de Financiación no
 * puede llamar a `PATCH /loans/:id` ni a `DELETE /expenses/:id`, que llevaban escritos y
 * probados desde la fase 2A sin ninguna interfaz que los usara.
 *
 * Los dos conviven a propósito: el desglose responde «¿cuánto me cuesta este piso al mes?» y
 * esto responde «¿qué contratos lo componen?».
 */
export interface LoanDto {
  id: Uuid;
  lender: string;
  principalAmount: Money;
  outstandingBalance: Money;
  /** Tipo nominal anual en %. */
  interestRate: number;
  rateType: RateType;
  startDate: IsoDate;
  endDate: IsoDate | null;
  monthlyPayment: Money;
  /** Porcentaje ya amortizado, derivado: no se guarda. */
  amortizedPercent: number;
}

export interface InsurancePolicyDto {
  id: Uuid;
  insurer: string;
  policyNumber: string | null;
  coverageType: string | null;
  premium: Money;
  startDate: IsoDate;
  endDate: IsoDate;
  /** Negativo si ya caducó. Es lo que la UI necesita para pintar el aviso. */
  daysToExpiry: number;
}

export interface RecurringExpenseDto {
  id: Uuid;
  concept: string;
  amount: Money;
  frequency: ExpenseFrequency;
  dayOfMonth: number | null;
  startDate: IsoDate;
  endDate: IsoDate | null;
  active: boolean;
  unit: UnitRef | null;
  /** Importe normalizado a mensual, con la misma fórmula que el desglose de gastos. */
  monthlyAmount: Money;
}

export interface PropertyFinancing {
  loans: LoanDto[];
  insurance: InsurancePolicyDto[];
  recurringExpenses: RecurringExpenseDto[];
}

export interface DocumentListRow {
  id: Uuid;
  name: string;
  category: DocumentCategory;
  propertyName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  issuedAt: IsoDate | null;
  uploadedAt: IsoDateTime;
  ocrStatus: OcrStatus;
}

/** Una línea de los costes de compra de un inmueble. */
export interface AcquisitionCostItem {
  concept: string;
  amount: Money;
}

export interface PropertyDetailResponse {
  id: Uuid;
  portfolio: { id: Uuid; name: string };
  name: string;
  addressLine: string;
  city: string;
  province: string | null;
  postalCode: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  propertyType: PropertyType;
  status: PropertyStatus;
  builtYear: number | null;
  areaM2: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  acquisitionDate: IsoDate | null;
  acquisitionPrice: Money;
  /** Suma de `acquisitionCostItems`. */
  acquisitionCosts: Money;
  /** Desglose de los costes de compra, en el orden en que se añadieron. */
  acquisitionCostItems: AcquisitionCostItem[];
  /** Frase de cabecera: `Valencia · Adquirido en marzo 2018 por 590.000 €`. */
  subtitle: string;
  // --- Fase 2 -------------------------------------------------------------
  floor?: string | null;
  door?: string | null;
  cadastralReference?: string | null;
  energyRating?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | null;
  energyRatingExpiresAt?: IsoDate | null;
  /**
   * Fiabilidad del pin del mapa. Es la diferencia entre «esta es la casa» y «esto es el
   * centro del pueblo», y la UI tiene que decirlo cuando no es exacta.
   */
  geocodePrecision?:
    | 'exacta'
    | 'calle'
    | 'municipio'
    | 'manual'
    | 'sin_geocodificar';
  geocodedAt?: IsoDateTime | null;
  /** Bloqueo optimista: se devuelve al cliente y vuelve en el `PATCH` como `If-Unmodified-Since`. */
  updatedAt?: IsoDateTime;
  notes: string | null;
  /** Si la ficha enseña la pestaña «Financiación». Opcional por inmueble. */
  financingEnabled: boolean;
  photos: PropertyPhotoDto[];
  units: UnitDto[];
  valuation: {
    current: Money;
    gainSincePurchase: Money;
    gainPercent: number;
    latest: ValuationDto | null;
    history: ValuationDto[];
  };
  financials: {
    debt: Money;
    equity: Money;
    monthlyRent: Money;
    monthlyExpenses: Money;
    cashFlow: Money;
    grossYield: number;
    capRate: number;
    occupancyRate: number;
  };
  expenseBreakdown: {
    lines: MonthlyExpenseLine[];
    total: Money;
  };
  documents: DocumentListRow[];
  /** Documentos de inmueble esperados y si existen: alimenta la lista con ✓ del maquetado. */
  documentChecklist: { label: string; present: boolean }[];
  /** Préstamos, seguros y gastos recurrentes con identificador, para poder editarlos. */
  financing: PropertyFinancing;
}

// ---------------------------------------------------------------------------
// GET /api/tenants
// ---------------------------------------------------------------------------

/** Estado de pago derivado del rent roll del inquilino. */
export type TenantPaymentStatus = 'al_dia' | 'pendiente' | 'impago';

/**
 * Contrato vigente al que le quedan menos de tres meses (o está ya dentro de su preaviso, si es
 * más largo) y no tiene subida ni la renovación ni la rescisión. `null` si no hay de qué avisar.
 * La misma regla enciende la alerta, el aviso de la ficha y el punto de la tarjeta.
 */
export interface LeaseExpiry {
  endDate: IsoDate;
  /** Negativo si la fecha de fin ya pasó y el contrato sigue activo. */
  daysToEnd: number;
}

export interface TenantCard {
  id: Uuid;
  fullName: string;
  initials: string;
  idDocumentType: IdDocumentType;
  idDocument: string | null;
  nationality: string | null;
  phone: string | null;
  email: string | null;
  /** `Calle Mayor 12 · Hab. 2` */
  placement: string | null;
  property: PropertyRef | null;
  unit: UnitRef | null;
  /** Número de habitación tal y como se muestra en la ficha. */
  room: string | null;
  monthlyRent: Money | null;
  leaseStatus: LeaseStatus | null;
  leaseEndDate: IsoDate | null;
  leaseExpiry: LeaseExpiry | null;
  paymentStatus: TenantPaymentStatus;
  paymentStatusLabel: string;
  /** Derivados de `documents`, nunca almacenados. */
  documentBadges: { label: string; present: boolean }[];
  /** Los dados de baja salen en «Todos» (`?historic=true`) con su etiqueta. */
  deletedAt: IsoDateTime | null;
  anonymized: boolean;
}

// ---------------------------------------------------------------------------
// GET /api/transactions
// ---------------------------------------------------------------------------

export interface TransactionRow {
  id: Uuid;
  bookedAt: IsoDate;
  description: string;
  counterparty: string | null;
  /** `Suministros · Electricidad`, ya compuesto padre · hijo. */
  categoryPath: string | null;
  categoryId: Uuid | null;
  categoryKind: CategoryKind | null;
  property: PropertyRef | null;
  unit: UnitRef | null;
  amount: Money;
  currency: string;
  source: TransactionSource;
  aiCategorized: boolean;
  aiConfidence: number | null;
  reviewStatus: ReviewStatus;
  matched: boolean;
  bankAccount: { id: Uuid; alias: string } | null;
  notes: string | null;
}

export interface TransactionsResponse extends Paginated<TransactionRow> {
  totals: {
    income: Money;
    expenses: Money;
    net: Money;
  };
  needsReviewCount: number;
}

// ---------------------------------------------------------------------------
// GET /api/rent-charges
// ---------------------------------------------------------------------------

export interface RentRollRow {
  id: Uuid;
  leaseId: Uuid;
  tenant: { id: Uuid; fullName: string; initials: string } | null;
  property: PropertyRef | null;
  unit: UnitRef | null;
  concept: string;
  periodStart: IsoDate;
  periodEnd: IsoDate;
  dueDate: IsoDate;
  /** `1 ago`, `20 jul` — como en el maquetado. */
  dueLabel: string;
  amount: Money;
  paidAmount: Money;
  status: RentChargeStatus;
  statusLabel: string;
  daysOverdue: number;
  /** `true` si el cargo viene arrastrado de un mes anterior sin cobrar. */
  carriedOver: boolean;
}

export interface RentChargesResponse {
  month: IsoMonth;
  monthLabel: string;
  data: RentRollRow[];
  totals: {
    expected: Money;
    collected: Money;
    collectedPercent: number;
    pending: Money;
    pendingCount: number;
    overdue: Money;
    overdueCount: number;
    overdueTenants: number;
    maxDaysOverdue: number;
  };
}

// ---------------------------------------------------------------------------
// GET /api/maintenance/issues
// ---------------------------------------------------------------------------

export interface MaintenanceIssueCard {
  id: Uuid;
  title: string;
  description: string | null;
  priority: IssuePriority;
  priorityLabel: string;
  status: IssueStatus;
  property: PropertyRef | null;
  unit: UnitRef | null;
  /** `Calle Mayor 12 · Hab. 2` */
  placement: string;
  vendor: { id: Uuid; name: string; trade: string | null } | null;
  scheduledAt: IsoDateTime | null;
  resolvedAt: IsoDateTime | null;
  cost: Money | null;
  createdAt: IsoDateTime;
  /** `Hace 2 días`, `Cita: mañana`, `Hace 1 sem.` — la etiqueta de la esquina de la tarjeta. */
  timeLabel: string;
  /** Distintivo de la esquina superior izquierda: prioridad, oficio o «Resuelta». */
  badge: { label: string; tone: 'danger' | 'warning' | 'success' | 'neutral' };
}

export interface MaintenanceColumn {
  status: IssueStatus;
  label: string;
  count: number;
  issues: MaintenanceIssueCard[];
}

export interface MaintenanceIssuesResponse {
  columns: MaintenanceColumn[];
  totals: { open: number; inProgress: number; resolved: number; averageCost: Money | null };
}

// ---------------------------------------------------------------------------
// GET /api/alerts
// ---------------------------------------------------------------------------

export interface AlertRow {
  id: Uuid;
  type: AlertType;
  /** Etiqueta del distintivo: `Impago`, `Vencimiento`, `Seguro`… */
  tag: string;
  severity: AlertSeverity;
  title: string;
  body: string;
  status: AlertStatus;
  property: PropertyRef | null;
  tenant: { id: Uuid; fullName: string } | null;
  createdAt: IsoDateTime;
  resolvedAt: IsoDateTime | null;
  payload: Record<string, unknown> | null;
}

export interface AlertsResponse {
  data: AlertRow[];
  counts: Record<AlertSeverity, number>;
}

// ---------------------------------------------------------------------------
// GET /api/reports
// ---------------------------------------------------------------------------

export interface ReportCatalogItem {
  id: string;
  name: string;
  description: string;
  /** Formatos previstos para la fase 2. */
  formats: ('pdf' | 'xlsx' | 'csv')[];
  /** Vista SQL o servicio que lo alimentará. */
  source: string;
  available: boolean;
}

// ---------------------------------------------------------------------------
// GET /api/portal/me
// ---------------------------------------------------------------------------

export interface PortalMessage {
  id: Uuid;
  body: string;
  sentAt: IsoDateTime;
  /** `tenant` = lo escribió el inquilino; `manager` = el propietario/gestor. */
  from: 'tenant' | 'manager';
  authorName: string;
  readAt: IsoDateTime | null;
}

export interface PortalIssue {
  id: Uuid;
  title: string;
  status: IssueStatus;
  statusLabel: string;
  /** Color del punto de la lista, resuelto por estado. */
  tone: 'warning' | 'success' | 'neutral' | 'danger';
}

export interface PortalMeResponse {
  tenant: {
    id: Uuid;
    fullName: string;
    initials: string;
    placement: string;
  };
  nextPayment: {
    id: Uuid;
    amount: Money;
    dueDate: IsoDate;
    /** `1 de agosto` */
    dueLabel: string;
    status: RentChargeStatus;
  } | null;
  issues: PortalIssue[];
  conversation: {
    id: Uuid;
    subject: string | null;
    messages: PortalMessage[];
  } | null;
}

// ---------------------------------------------------------------------------
// Utilidades de dominio compartidas
// ---------------------------------------------------------------------------

export const MONTH_LABELS_ES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
] as const;

export const MONTH_NAMES_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** Etiqueta corta de mes (`Ene`) a partir de un `YYYY-MM`. */
export function monthShortLabel(isoMonth: IsoMonth): string {
  const index = Number(isoMonth.slice(5, 7)) - 1;
  return MONTH_LABELS_ES[index] ?? isoMonth;
}

// ---------------------------------------------------------------------------
// Fase 2 — validación compartida y formas de respuesta nuevas
//
// Se re-exporta desde aquí para que tanto la API como la web sigan importando de
// `@naar/shared` y no de rutas internas del paquete.
// ---------------------------------------------------------------------------

export * from './validation/identity';
export * from './validation/iban';
export * from './validation/primitives';
export * from './schemas/auth';
export * from './schemas/property';
export * from './schemas/tenant';
export * from './schemas/lease';
export * from './schemas/ledger';
export * from './dto';
